import React, { useEffect, useState } from 'react';
import { post, put } from "../../utils/fetchAPI";
import { Bounce, toast } from "react-toastify";

const PermissionForm = ({ permission, defaultModule, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'admin-portal',
        module: '',
        status: 'active'
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (permission) {
            setFormData({
                name: permission.name || '',
                type: permission.type || 'admin-portal',
                module: permission.module || '',
                status: permission.status || 'active'
            });
        } else {
            setFormData({
                name: '',
                type: 'admin-portal',
                module: defaultModule || '',
                status: 'active'
            });
        }
    }, [permission, defaultModule]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (permission) {
                // Update existing permission
                const updateData = {
                    name: formData.name
                };
                const response = await put(`/permission/${permission._id}`, updateData);
                toast.success('Permission updated successfully!', {
                    position: "top-center",
                    theme: "light",
                    transition: Bounce,
                    hideProgressBar: false,
                    draggable: true,
                });
                onSuccess(response.data);
            } else {
                // Create new permission
                const response = await post('/permission/create', formData);
                toast.success("Permission created successfully!", {
                    position: "top-center",
                    theme: "light",
                    transition: Bounce,
                    hideProgressBar: false,
                    draggable: true,
                });
                onSuccess(response.data);
            }
            onClose();
        } catch (error) {
            console.error("Error saving permission:", error);
            toast.error(
                error?.response?.data?.message || 
                `Failed to ${permission ? 'update' : 'create'} permission. Please try again.`,
                {
                    position: "top-center",
                    theme: "light",
                    hideProgressBar: true,
                    draggable: true,
                }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='py-6 min-[320px]:py-2 lg:px-5 min-[320px]:px-2'>
            <h2 className="text-primary lg:text-3xl sm:text-lg font-bold mb-4">
                {permission ? "Edit Permission" : "Create New Permission"}
            </h2>
            <form 
                className='text-black lg:text-sm min-[320px]:text-xs flex flex-col w-full min-[320px]:max-h-[30rem] overflow-y-auto' 
                onSubmit={handleSubmit}
            >
                {/* Name Field */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium">Name <span className="text-red-500">*</span></label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., User create"
                        className="text-sm border-2 border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        required
                    />
                </div>

                {/* Type Field - Only for Create */}
                {!permission && (
                    <div className="mb-4">
                        <label className="block mb-2 font-medium">Type <span className="text-red-500">*</span></label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            className="text-sm border-2 border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            required
                        >
                            <option value="admin-portal">Admin Portal</option>
                            <option value="user-portal">User Portal</option>
                        </select>
                    </div>
                )}

                {/* Module Field - Only for Create */}
                {!permission && (
                    <div className="mb-4">
                        <label className="block mb-2 font-medium">Module <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            name="module"
                            value={formData.module}
                            onChange={handleInputChange}
                            placeholder="e.g., user, order, report"
                            className="text-sm border-2 border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            required
                        />
                    </div>
                )}

                {/* Status Field - Only for Create */}
                {!permission && (
                    <div className="mb-4">
                        <label className="block mb-2 font-medium">Status <span className="text-red-500">*</span></label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            className="text-sm border-2 border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            required
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                )}

                {/* Display read-only fields for Edit */}
                {permission && (
                    <>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium text-gray-600">Type</label>
                            <input
                                type="text"
                                value={permission.type || ''}
                                className="text-sm border-2 border-gray-200 rounded-md p-2 w-full bg-gray-50"
                                disabled
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium text-gray-600">Module</label>
                            <input
                                type="text"
                                value={permission.module || ''}
                                className="text-sm border-2 border-gray-200 rounded-md p-2 w-full bg-gray-50"
                                disabled
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium text-gray-600">Status</label>
                            <input
                                type="text"
                                value={permission.status || ''}
                                className="text-sm border-2 border-gray-200 rounded-md p-2 w-full bg-gray-50"
                                disabled
                            />
                        </div>
                    </>
                )}

                {/* Buttons */}
                <div className="flex gap-5 mt-6">
                    <button 
                        type="submit" 
                        className="text-sm px-6 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : (permission ? 'Update' : 'Create')}
                    </button>
                    <button 
                        type="button" 
                        onClick={onClose}
                        className="text-sm px-6 py-2 bg-gray-300 text-black rounded-md hover:bg-gray-400 transition-colors"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PermissionForm;

